import get from 'lodash/get.js';
import has from 'lodash/has.js';
import merge from 'lodash/merge.js';
import unset from 'lodash/unset.js';

import { handleResponseError } from '@/Helpers.js';
import CourseModel from '@/models/Course.js';
import CourseEnrollment from '@/models/CourseEnrollment.js';
import axios from '@/plugins/axios.js';
import MenuService from '@/services/MenuService.js';
import Course from '@/store/modules/courses/Course.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';
import { promiseError } from '@/store/utilities/Helpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

const collectionHelpers = EdverumTable.collection('course');

export default merge(Collection('Courses', Course), {
    state: {
        _enrollments: [],
        loadingEnrollmentsAsync: true,
        sort: {
            title: 'asc',
        },
        // PHASE 2 ADDITION: Track view-specific loading states
        viewLoadingStates: {
            default: false,
            gradebook: false,
            enrollments: false,
            activities: false,
            menu: false,
        },
        // PHASE 2 ADDITION: Cache for menu data
        menuCache: {},
    },

    getters: {
        ...collectionHelpers.getters,

        all(state) {
            return () => {
                const items = { ...state };

                unset(items, 'sort');
                unset(items, 'loading');
                unset(items, 'paginate');
                unset(items, '_enrollments');
                unset(items, 'loadingEnrollmentsAsync');
                unset(items, 'viewLoadingStates');
                unset(items, 'menuCache');

                return items;
            };
        },

        // PHASE 2 ADDITION: Getters for view-specific loading states
        isViewLoading: state => view => {
            return state.viewLoadingStates[view] || false;
        },

        hasMenuCache: state => courseId => {
            return has(state.menuCache, courseId);
        },

        getMenuCache: state => courseId => {
            return get(state.menuCache, courseId);
        },
    },

    actions: {
        clearSort: collectionHelpers.actions.clearSort,
        paginateAndFetch: collectionHelpers.actions.paginateAndFetch,

        // Courses index does not need ?menu=true; course pages load menu via MenuService.
        fetch: collectionHelpers.actions.fetch(({ context, queryBuilder }) => {
            const { getters, rootGetters } = context;

            queryBuilder
                .include(['tags', 'groups', 'students', 'instructors', 'courseCodes'])
                .append('active_student_count')
                .sort(getters.queryBuilderSorting)
                .setPagination(getters.paginate);

            if (rootGetters['CoursesView/hasQuery']) {
                queryBuilder.raw({
                    search: rootGetters['CoursesView/searchQuery'],
                });
            }

            if (rootGetters['CoursesView/hasTagSearch']) {
                queryBuilder.raw({
                    tags: rootGetters['CoursesView/searchTag'],
                });
            }

            if (rootGetters['CoursesView/hasGroupSearch']) {
                queryBuilder.raw({
                    groups: rootGetters['CoursesView/searchGroup'],
                });
            }

            if (rootGetters['CoursesView/hasCourseCodeSearch']) {
                queryBuilder.raw({
                    course_code: rootGetters['CoursesView/searchCourseCode'],
                });
            }

            if (rootGetters['CoursesView/hasCourseTypeSearch']) {
                queryBuilder.raw({
                    course_type_id: rootGetters['CoursesView/searchCourseType'],
                });
            }

            if (rootGetters['CoursesView/hasInstructorSearch']) {
                queryBuilder.raw({
                    instructor: rootGetters['CoursesView/searchInstructor'],
                });
            }

            if (rootGetters['CoursesView/hasInProgress']) {
                queryBuilder.raw({
                    in_progress: rootGetters['CoursesView/inProgress'],
                });
            }

            if (rootGetters['CoursesView/hasCourseKeyFilter']) {
                queryBuilder.filter('course_key_enabled', rootGetters['CoursesView/filterCourseKey']);
            }
        },{ withMenu: false }),

        /**
         *
         * @param context
         * @param payload
         * @returns {Promise<any>}
         */
        async fetchOne(context, payload) {
            // Validate courseId to prevent API calls with undefined/invalid values
            // This catches issues where route params aren't available yet or navigation race conditions
            // We silently return null to avoid showing error snackbars to users for this race condition
            const courseId = Number(payload.id);

            if (!courseId || !Number.isFinite(courseId) || courseId <= 0) {
                console.warn('Courses/fetchOne called with invalid courseId:', payload.id, '- silently ignoring');
                return null;
            }

            const shouldHydrateCourseForm = has(payload, 'hydrate_course_form');

            if (shouldHydrateCourseForm) {
                context.commit('CourseTemplateForm/SET_HYDRATING', true, { root: true });
            }

            try {
                const queryBuilder = new QueryBuilder(`courses/${courseId}`)
                    .include([
                        'gradebook',
                        'tags',
                        'groups',
                        'activities.rubric',
                        'attendancesCount',
                        'sections.upcomingTimePeriods.satisfiers',
                        'sections.upcomingTimePeriods.locations.parent',
                        'sections.upcomingTimePeriods.enrollments',
                        'sections.upcomingTimePeriods.enrollmentGroups.color',
                        'sections.objective.criterias',
                        'courseCodes',
                        'remediations',
                    ])
                    .append([
                        'completed',
                        'has_instructor',
                        'has_course_grades',
                        'has_activity_with_conflicting_minimum_passing_score',
                        'current_lock_status',
                        'current_lock_expires_at',
                    ])
                    .when(get(payload, 'attendance_scheduler_structure'), queryBuilder =>
                        queryBuilder.raw({
                            attendance_scheduler_structure: true,
                        })
                    )
                    .when(get(payload, 'exclude_submissions'), queryBuilder =>
                        queryBuilder.raw({ exclude_submissions: true })
                    );

                const { data } = await axios.get(queryBuilder.toUrl(), { errorHandle: false });

                await context.dispatch('add', data.data);

                if (shouldHydrateCourseForm && Array.isArray(data.data.items) && data.data.items.length > 0) {
                    context.commit(
                        'CourseTemplateForm/updateCourseField',
                        {
                            field: 'items',
                            value: data.data.items,
                        },
                        { root: true }
                    );
                }

                // Make the course available to Course.vue as soon as the API responds so the
                // full-page loader does not stay up through the entire hydrate cycle.
                await CourseModel.insert({ data: data.data });

                // Menu and form hydrate are independent once the course payload is in hand —
                // run them together to shave sequential wait time on edit opens.
                const postLoadTasks = [
                    MenuService.loadAndSetCourseMenu(context, courseId).catch(menuError => {
                        console.warn('Failed to load menu data:', menuError);
                    }),
                ];

                if (shouldHydrateCourseForm) {
                    postLoadTasks.push(
                        context.dispatch('CourseTemplateForm/hydrate', { payload: data.data }, { root: true })
                    );
                }

                await Promise.all(postLoadTasks);

                if (has(data, 'meta.attendance_scheduler_structure')) {
                    await context.dispatch(
                        'AttendanceScheduler/loadStructure',
                        get(data, 'meta.attendance_scheduler_structure'),
                        { root: true }
                    );
                }

                return data;
            } catch (error) {
                if (error?.response?.status === 403) {
                    throw error;
                }

                handleResponseError(error);

                promiseError({
                    key: 'course_load_error',
                    context,
                });

                return error;
            } finally {
                if (shouldHydrateCourseForm) {
                    context.commit('CourseTemplateForm/SET_HYDRATING', false, { root: true });
                }
            }
        },

        /**
         *
         * @param context
         * @param payload
         * @returns {Promise<any>}
         */
        async fetchDashboard(context, { id, loadEnrollmentsAsync }) {
            // Validate courseId to prevent API calls with undefined/invalid values
            // We silently return null to avoid showing error snackbars to users for this race condition
            const courseId = Number(id);
            if (!courseId || !Number.isFinite(courseId) || courseId <= 0) {
                console.warn('Courses/fetchDashboard called with invalid courseId:', id, '- silently ignoring');
                return null;
            }

            try {
                const queryBuilder = new QueryBuilder(`course-dashboard/${courseId}`)
                    .include([
                        'gradebook',
                        'tags',
                        'groups',
                        'activities.rubric',
                        'sections.upcomingTimePeriods.locations.parent',
                        'sections.upcomingTimePeriods.enrollments',
                        'sections.upcomingTimePeriods.enrollmentGroups.color',
                        'courseCodes',
                    ])
                    .append('completed');

                const { data } = await axios.get(queryBuilder.toUrl(), {
                    errorHandle: false,
                    ...context.rootGetters.deviceAssetHeader,
                });

                if (loadEnrollmentsAsync) {
                    const courseData = data.data;

                    const enrollments = [...data.data.enrollments];

                    // Store enrollments aside for async loading, but also insert them immediately
                    // so they're available for queries (like instructors list)
                    context.commit('SET_ENROLLMENTS_ASIDE', enrollments);

                    // Insert enrollments immediately so they're available for queries
                    if (enrollments.length > 0) {
                        await CourseEnrollment.insert({ data: enrollments });
                    }

                    delete courseData.enrollments;

                    CourseModel.insert({ data: courseData });
                } else {
                    await CourseModel.insert({ data: data.data });
                }

                await context.dispatch('add', data.data);

                // Hydrate CourseTemplateForm with course_type so useSelfPacedCourse() works on course dashboard.
                const courseData = data.data;
                if (courseData?.course_type != null) {
                    context.commit(
                        'CourseTemplateForm/updateCourseField',
                        { field: 'course_type', value: courseData.course_type },
                        { root: true }
                    );
                }
                if (courseData?.id != null) {
                    context.commit(
                        'CourseTemplateForm/updateCourseField',
                        { field: 'id', value: courseData.id },
                        { root: true }
                    );
                }

                // Load menu data using MenuService
                try {
                    await MenuService.loadAndSetCourseMenu(context, courseId);
                } catch (menuError) {
                    console.warn('Failed to load menu data:', menuError);
                }

                return data;
            } catch (error) {
                // Let 403 (access denied) propagate so Course.vue can show "Not Accessible" UI
                // instead of the generic Error Details modal and avoid rendering course content
                if (error?.response?.status === 403) {
                    throw error;
                }

                handleResponseError(error);

                promiseError({
                    key: 'course_load_error',
                    context,
                });

                return error;
            }
        },

        async finishLoadingEnrollments({ commit, state }) {
            commit('LOADING_ENROLLMENTS_ASYNC', true);

            if (state._enrollments.length > 0) {
                // Vuex ORM will automatically normalize nested relationships (user, roles) from the data
                await CourseEnrollment.insert({ data: state._enrollments });
                commit('SET_ENROLLMENTS_ASIDE', []);
            }

            commit('LOADING_ENROLLMENTS_ASYNC', false);
        },

        updateTags: collectionHelpers.actions.updateModelTags(),

        updateGroups: collectionHelpers.actions.updateModelGroups(),

        // PHASE 2 NEW: View-based fetch methods
        /**
         * PHASE 2 NEW: Fetch course with minimal data
         * Uses basic-information endpoint for lightweight requests
         */
        async fetchMinimal(context, { id }) {
            try {
                context.commit('SET_VIEW_LOADING', { view: 'minimal', loading: true });

                const queryBuilder = new QueryBuilder(`courses/${id}`).raw({ 'basic-information': true });

                const { data } = await axios.get(queryBuilder.toUrl(), { errorHandle: false });

                await context.dispatch('add', data.data);
                await CourseModel.insert({ data: data.data });

                return data;
            } catch (error) {
                handleResponseError(error);
                promiseError({ key: 'course_load_error', context });
                return error;
            } finally {
                context.commit('SET_VIEW_LOADING', { view: 'minimal', loading: false });
            }
        },

        /**
         * PHASE 2 OPTIMIZED: View-based course fetching
         * Leverages Phase 1 API view parameter for optimized loading
         */
        async fetchForView(context, { id, view = 'default', ...options }) {
            try {
                context.commit('SET_VIEW_LOADING', { view, loading: true });

                const queryBuilder = new QueryBuilder(`courses/${id}`).raw({ view });

                // Add optional parameters
                if (options.attendance_scheduler_structure) {
                    queryBuilder.raw({ attendance_scheduler_structure: true });
                }

                if (options.exclude_submissions) {
                    queryBuilder.raw({ exclude_submissions: true });
                }

                const { data } = await axios.get(queryBuilder.toUrl(), { errorHandle: false });

                await context.dispatch('add', data.data);

                // Handle form hydration if needed
                if (has(options, 'hydrate_course_form')) {
                    await context.dispatch('CourseTemplateForm/hydrate', { payload: data.data }, { root: true });
                }

                // Handle attendance scheduler structure
                if (has(data, 'meta.attendance_scheduler_structure')) {
                    await context.dispatch(
                        'AttendanceScheduler/loadStructure',
                        get(data, 'meta.attendance_scheduler_structure'),
                        { root: true }
                    );
                }

                await CourseModel.insert({ data: data.data });

                return data;
            } catch (error) {
                handleResponseError(error);
                promiseError({ key: 'course_load_error', context });
                return error;
            } finally {
                context.commit('SET_VIEW_LOADING', { view, loading: false });
            }
        },

        /**
         * PHASE 2 NEW: Dedicated gradebook data fetching
         */
        async fetchForGradebook(context, { id, ...options }) {
            return context.dispatch('fetchForView', {
                id,
                view: 'gradebook',
                ...options,
            });
        },

        /**
         * PHASE 2 NEW: Dedicated enrollments data fetching
         */
        async fetchForEnrollments(context, { id, ...options }) {
            return context.dispatch('fetchForView', {
                id,
                view: 'enrollments',
                ...options,
            });
        },

        /**
         * PHASE 2 NEW: Dedicated activities data fetching
         */
        async fetchForActivities(context, { id, ...options }) {
            return context.dispatch('fetchForView', {
                id,
                view: 'activities',
                ...options,
            });
        },

        /**
         * PHASE 2 NEW: Cached menu data fetching
         */
        async fetchMenu(context, { id, force = false }) {
            try {
                // Check cache first (unless forced)
                if (!force && context.getters.hasMenuCache(id)) {
                    const cached = context.getters.getMenuCache(id);
                    const cacheAge = Date.now() - cached.timestamp;

                    // Use cache if less than 5 minutes old
                    if (cacheAge < 300000) {
                        return { data: cached.data };
                    }
                }

                context.commit('SET_VIEW_LOADING', { view: 'menu', loading: true });

                const { data } = await axios.get(`/api/courses/${id}/menu`, { errorHandle: false });

                // Cache the menu data
                context.commit('SET_MENU_CACHE', {
                    courseId: id,
                    menuData: data.data,
                });

                return data;
            } catch (error) {
                handleResponseError(error);
                promiseError({ key: 'menu_load_error', context });
                return error;
            } finally {
                context.commit('SET_VIEW_LOADING', { view: 'menu', loading: false });
            }
        },

        /**
         * Clear menu cache for a specific course or all courses
         */
        clearMenuCache(context, courseId = null) {
            context.commit('CLEAR_MENU_CACHE', courseId);
        },
    },

    mutations: {
        UPDATE_TAGS: collectionHelpers.mutations.UPDATE_TAGS,
        UPDATE_GROUPS: collectionHelpers.mutations.UPDATE_GROUPS,
        CLEAR_SORTING: collectionHelpers.mutations.CLEAR_SORTING,

        SET_ENROLLMENTS_ASIDE: (state, enrollments) => {
            state._enrollments = enrollments;
        },

        // PHASE 2 ADDITION: Mutations for view loading states and cache
        SET_VIEW_LOADING: (state, { view, loading }) => {
            state.viewLoadingStates[view] = loading;
        },

        SET_MENU_CACHE: (state, { courseId, menuData }) => {
            state.menuCache[courseId] = {
                data: menuData,
                timestamp: Date.now(),
            };
        },

        CLEAR_MENU_CACHE: (state, courseId) => {
            if (courseId) {
                delete state.menuCache[courseId];
            } else {
                state.menuCache = {};
            }
        },

        LOADING_ENROLLMENTS_ASYNC: (state, loading) => {
            state.loadingEnrollmentsAsync = loading;
        },
    },
});
